import { PublicClientApplication, Configuration, LogLevel, BrowserCacheLocation } from "@azure/msal-browser";

const clientId = process.env.REACT_APP_AZURE_AD_CLIENT_ID || "";
const tenantId = process.env.REACT_APP_AZURE_AD_TENANT_ID || "";

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri:
      process.env.REACT_APP_AZURE_AD_REDIRECT_URI ||
      (typeof window !== "undefined" ? `${window.location.origin}/login` : "/login"),
  },
  cache: {
    // MSAL's own cache is per-tab. Cross-tab silent SSO relies on the browser's
    // Microsoft session cookies (login.microsoftonline.com), not this cache —
    // which is exactly what makes silent login from the App Launcher work.
    cacheLocation: BrowserCacheLocation.SessionStorage,
  },
  system: {
    loggerOptions: {
      loggerCallback: () => {},
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
    },
  },
};

// Scopes for the silent / interactive login. openid+profile+email give us the
// ID token (with the email claim) that the backend validates against JWKS.
export const msLoginRequest = { scopes: ["openid", "profile", "email"] };

// --- Login-page SSO state-machine keys ---
// Suppress flag: set after a silent probe fails so we don't loop probing.
export const MS_SSO_SUPPRESS_KEY = "ms_sso_suppress";
// URL param appended on an *explicit* logout so we stay on the form (no auto re-login).
export const MS_SSO_LOGGED_OUT_PARAM = "loggedout";
// Break-glass entry point: `/login?emergency=1` shows the email + password form
// immediately, without initializing MSAL or probing Microsoft at all. This is the
// documented path for a Microsoft/Entra outage — if Microsoft is unreachable, the
// normal `/login` redirects away to login.microsoftonline.com and may never come
// back, so no link rendered on our own login card would be reachable. A URL param
// (not sessionStorage) for the same reason as MS_SSO_LOGGED_OUT_PARAM: it lives
// only on this tab's URL and is NOT copied into a newly opened tab.
export const MS_SSO_EMERGENCY_PARAM = "emergency";
// Tracks whether the in-flight redirect was a silent or interactive attempt.
export const MS_SSO_MODE_KEY = "ms_sso_mode";

export const msalInstance = new PublicClientApplication(msalConfig);

// MSAL v3+ requires a one-time initialize() before any other API call.
let initPromise: Promise<void> | null = null;
export const ensureMsalInitialized = () => {
  if (!initPromise) initPromise = msalInstance.initialize();
  return initPromise;
};
